import { useAuth } from "@/contexts/AuthContext";
import { useUnlocks } from "@/hooks/useUnlocks";
import { useAssessmentState } from "@/hooks/useAssessmentState";

export type AssessmentCtaState =
  | "loading"
  | "no_assessment"     // not logged in OR logged in but assessment not complete
  | "completed_locked"  // assessment done, matches NOT purchased
  | "completed_unlocked"; // assessment done, matches purchased (or higher tier)

/**
 * Single source of truth for personalised CTA logic across the platform.
 *
 * Rule: once `hasCompletedAssessment` is true, NEVER show "Take the assessment".
 * Show an unlock CTA (locked) or a "view your results" CTA (unlocked) instead.
 */
export function useAssessmentCtaState(): {
  state: AssessmentCtaState;
  isLoggedIn: boolean;
  hasCompletedAssessment: boolean;
  hasMatchesUnlock: boolean;
} {
  const { user, loading: authLoading } = useAuth();
  const { matches, blueprint, accelerator, loading: unlocksLoading } = useUnlocks();
  const assessment = useAssessmentState();

  const hasCompletedAssessment = assessment.status === "completed";
  const hasMatchesUnlock = matches || blueprint || accelerator;

  let state: AssessmentCtaState;
  if (authLoading) {
    state = "loading";
  } else if (!hasCompletedAssessment) {
    state = "no_assessment";
  } else if (unlocksLoading) {
    state = "completed_locked";
  } else if (hasMatchesUnlock) {
    state = "completed_unlocked";
  } else {
    state = "completed_locked";
  }

  return {
    state,
    isLoggedIn: !!user,
    hasCompletedAssessment,
    hasMatchesUnlock,
  };
}
